/** Asset register list with URL-backed filters, sorting and pagination. */
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { useSites } from "@/features/admin/reference/queries";
import { AssetFormDialog } from "@/features/assets/AssetFormDialog";
import {
  ASSET_STATUSES,
  type Asset,
  type AssetFilters,
  useAssetPage,
  useAssetTypes,
} from "@/features/assets/queries";
import { DataTable } from "@/shared/components/data-table";
import { downloadTableExport, TableExportButton } from "@/shared/components/table-export-button";
import { TableToolbar, type TableFilterDefinition } from "@/shared/components/table-toolbar";
import { bindTableFilters, tableSortFields } from "@/shared/lib/table-controls";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "@/shared/components/page";
import { formatRelative } from "@/shared/lib/format";
import { PAGE_SIZES, useListState } from "@/shared/lib/list-state";
import { Button } from "@/shared/ui/button";

const CRITICALITIES = ["low", "medium", "high"] as const;

/** Asset list page. */
export function AssetsPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const list = useListState("/assets", "code");
  const { search, state } = list;
  const [createOpen, setCreateOpen] = useState(false);
  const filters: AssetFilters = {
    q: search.q,
    status: search.status,
    vessels_only: search.vessels_only,
    type_key: search.type_key,
    site_id: search.site_id,
    criticality: search.criticality,
    ownership: search.ownership,
  };
  const page = useAssetPage(filters, state);
  const types = useAssetTypes();
  const sites = useSites();

  const field = bindTableFilters(filters, list.update);
  const definitions: TableFilterDefinition[] = [
    {
      ...field("status", t("app.status")),
      kind: "multi",
      prominent: true,
      options: ASSET_STATUSES.map((value) => ({ value, label: t(`assets.status.${value}`) })),
    },
    {
      ...field("type_key", t("app.type")),
      kind: "select",
      prominent: true,
      options: (types.data ?? []).map((type) => ({ value: type.key, label: type.name })),
    },
    {
      ...field("site_id", t("app.site")),
      kind: "select",
      options: (sites.data ?? []).map((site) => ({ value: site.id, label: site.name })),
    },
    {
      ...field("criticality", t("assets.criticality")),
      kind: "select",
      options: CRITICALITIES.map((value) => ({
        value,
        label: t(`assets.criticalityLevel.${value}`),
      })),
    },
    {
      ...field("ownership", t("assets.ownership")),
      kind: "select",
      options: ["owned", "external"].map((value) => ({
        value,
        label: t(`assets.ownershipScope.${value}`),
      })),
    },
    {
      ...field("vessels_only", t("assets.vesselsOnly")),
      kind: "boolean",
      active: !!filters.vessels_only,
      removeLabel: t("assets.vesselsOnly"),
    },
  ];

  const columns: ColumnDef<Asset>[] = [
    {
      id: "code",
      enableSorting: true,
      header: t("app.code"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.code}</span>,
    },
    {
      id: "name",
      enableSorting: true,
      header: t("app.name"),
      cell: (c) => <AssetReference id={c.row.original.id} name={c.row.original.name} />,
    },
    {
      id: "type",
      enableSorting: true,
      header: t("app.type"),
      cell: (c) => c.row.original.type.name,
    },
    {
      id: "site",
      enableSorting: true,
      header: t("app.site"),
      cell: (c) => c.row.original.site?.name ?? "—",
    },
    {
      id: "status",
      enableSorting: true,
      header: t("app.status"),
      cell: (c) => <StatusBadge value={c.row.original.status} namespace="assets.status" />,
    },
    {
      id: "criticality",
      enableSorting: true,
      header: t("assets.criticality"),
      cell: (c) => (
        <StatusBadge value={c.row.original.criticality} namespace="assets.criticalityLevel" />
      ),
    },
    {
      id: "current_position_at",
      header: t("assets.position"),
      cell: (c) => formatRelative(c.row.original.current_position_at),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t("assets.title")}
        actions={
          can("assets.asset.create") ? (
            <Button onClick={() => setCreateOpen(true)}>{t("assets.new")}</Button>
          ) : null
        }
      />
      {createOpen ? <AssetFormDialog open={createOpen} onOpenChange={setCreateOpen} /> : null}
      <TableToolbar
        filters={definitions}
        search={search.q}
        searchPlaceholder={t("tablePilot.assetSearch")}
        searchDescription={t("tablePilot.assetSearchScope")}
        onSearchChange={(q) => list.update({ q })}
        sorting={state.sort}
        sortFields={tableSortFields(columns)}
        onSortingChange={list.setSort}
        onReset={() =>
          list.update({
            q: undefined,
            status: undefined,
            type_key: undefined,
            site_id: undefined,
            criticality: undefined,
            ownership: undefined,
            vessels_only: undefined,
          })
        }
        actions={
          <TableExportButton
            onExport={(format) =>
              downloadTableExport(
                "/api/v1/assets/",
                "assets",
                { ...filters, ordering: state.sort },
                format,
              )
            }
          />
        }
      />
      {page.query.isPending ? (
        <LoadingState />
      ) : page.query.isError ? (
        <ErrorState error={page.query.error} onRetry={() => void page.query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          minWidth={900}
          data={page.items}
          getRowId={(a) => a.id}
          rowHref={(a) => `/assets/${a.id}`}
          sorting={state.sort}
          onSortingChange={list.setSort}
          isFetching={page.query.isFetching}
          pagination={{
            page: page.page,
            pages: page.pages,
            total: page.total,
            pageSize: page.pageSize,
            onPageChange: list.setPage,
            onPageSizeChange: list.setSize,
            sizes: PAGE_SIZES,
            extendedNavigation: true,
          }}
        />
      )}
    </div>
  );
}
